import styled from "styled-components";
import tw from "twin.macro";
import GradientButton from "../../../../components/GradientButton";
import { COLORS } from "../theme";

const SectionThree = styled.section`
  ${tw`relative flex items-center`}
  z-index: 1;
  min-height: 85vh;
  background: #fff;

  @media (max-width: 767px) {
    min-height: auto;
  }
`;

const SectionThreeInner = styled.div`
  ${tw`mx-auto`}
  width: 100%;
  padding: 2.5rem var(--app-content-gutter) 3rem;
  max-width: 1440px;

  @media (min-width: 1440px) {
    max-width: none;
    padding-left: 180px;
    padding-right: 180px;
  }

  @media (min-width: 1367px) and (max-width: 1540px) {
    padding-left: 120px;
    padding-right: 120px;
  }

  @media (min-width: 1024px) {
    padding-top: 3.5rem;
    padding-bottom: 5.5rem;
  }

  @media (max-width: 1366px) {
    padding-top: 2.5rem;
  }

  @media (min-width: 1181px) and (max-width: 1366px) {
    padding-left: 3rem;
    padding-right: 5.5rem;
    padding-bottom: 7rem;
  }

  @media (max-width: 767px) {
    padding: 5.5rem 0 5rem;
  }
`;

const SectionThreeGrid = styled.div`
  ${tw`grid gap-10 items-center`}

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 0.94fr) minmax(0, 1.06fr);
    column-gap: 5rem;
  }

  @media (min-width: 1024px) and (max-width: 1366px) {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    column-gap: 4.5rem;
  }

  @media (min-width: 1440px) {
    max-width: 1480px;
    margin: 0 auto;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    column-gap: 7rem;
  }

  @media (min-width: 1367px) and (max-width: 1540px) {
    max-width: 1220px;
    grid-template-columns: minmax(0, 0.86fr) minmax(0, 1.14fr);
    column-gap: 5.5rem;
  }

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
    row-gap: 2.5rem;
  }

  @media (max-width: 767px) {
    row-gap: 4.5rem;
  }
`;

const SectionThreeText = styled.div`
  ${tw`relative`}
  z-index: 2;
`;

const WarningRow = styled.div`
  ${tw`flex items-start`}
  gap: 1.5rem;

  @media (max-width: 767px) {
    justify-content: center;
    padding: 0 2rem;
    text-align: center;
  }
`;

const WarningMark = styled.div`
  margin: 0 0 0 2.2rem;
  font-family: "Montserrat", sans-serif;
  font-size: 8rem;
  line-height: 0.82;
  font-weight: 700;
  color: ${COLORS.orange};

  @media (max-width: 1366px) {
    font-size: 6rem;
    margin-left: 0.8rem;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const WarningTitle = styled.h3`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 20px;
  line-height: 1.08;
  font-weight: 400;
  color: #555;
  text-transform: uppercase;

  strong {
    color: ${COLORS.orange};
    font-weight: 700;
  }

  @media (max-width: 767px) {
    font-size: 20px;
    line-height: 1.16;
  }
`;

const BulletList = styled.ul`
  margin: 2rem 0 0 0;
  padding: 0;
  list-style: disc;
  color: #4d4b4b;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 1.18;
  font-weight: 700;

  @media (max-width: 1366px) {
    font-size: 18px;
    margin-top: 1.5rem;
  }

  @media (max-width: 767px) {
    width: fit-content;
    margin: 2.75rem auto 0;
    text-align: left;
    font-size: 16px;
    line-height: 1.42;
    font-weight: 400;
  }
`;

const BulletItem = styled.li`
  margin-top: 0.3rem;
`;

const SectionThreeCta = styled(GradientButton)`
  margin-top: 2.2rem;
  min-width: 420px;
  max-width: 100%;
  padding: 15px 30px;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 1.15;
  font-weight: 500;

  @media (max-width: 1366px) {
    min-width: 360px;
  }

  @media (max-width: 767px) {
    margin-top: 3.25rem;
    min-width: 0;
    width: min(500px, calc(100vw - 84px));
    height: 54px;
    padding: 0 16px;
    font-size: 16px;
    white-space: nowrap;
  }
`;

const SectionThreeVisualColumn = styled.div`
  @media (max-width: 767px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
  }
`;

const SectionThreeVisual = styled.div`
  ${tw`relative`}
  min-height: 360px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (min-width: 1440px) {
    transform: none;
    min-height: 480px;
  }

  @media (min-width: 1024px) {
    min-height: 460px;
  }

  @media (min-width: 1367px) and (max-width: 1540px) {
    min-height: 420px;
  }

  @media (max-width: 1366px) {
    min-height: 380px;
    transform: translateX(-1.5rem);
  }

  @media (max-width: 1180px) {
    min-height: 0;
    transform: none;
  }

  @media (max-width: 767px) {
    min-height: 390px;
  }
`;

const RectangleBack = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 3.7rem;
  width: 820px;
  height: 320px;
  z-index: 0;
  border-radius: 0 0 88px 0;
  background: linear-gradient(180deg, #ffffff 0%, #d9ecec 36%, #6da09e 100%);
  box-shadow: 0 10px 14px rgba(0, 0, 0, 0.12);

  @media (min-width: 1440px) {
    width: 760px;
    height: 300px;
  }

  @media (min-width: 1367px) and (max-width: 1540px) {
    top: 3.2rem;
    width: 650px;
    height: 270px;
  }

  @media (max-width: 1366px) {
    top: 3.1rem;
    width: 620px;
    height: 260px;
  }

  @media (max-width: 1180px) {
    width: min(92vw, 760px);
    height: 290px;
    top: 3rem;
  }

  @media (max-width: 767px) {
    width: min(92vw, 620px);
    height: 330px;
    top: 98px;
    border-radius: 0 0 54px 0;
  }
`;

const ForegroundImage = styled.img`
  position: relative;
  z-index: 1;
  width: 760px;
  height: auto;
  display: block;
  margin: 0 auto;

  @media (min-width: 1440px) {
    width: 680px;
  }

  @media (min-width: 1367px) and (max-width: 1540px) {
    width: 600px;
  }

  @media (max-width: 1366px) {
    width: 560px;
  }

  @media (max-width: 1180px) {
    width: min(86vw, 680px);
  }

  @media (max-width: 767px) {
    width: min(86vw, 572px);
  }
`;

const PalmTitle = styled.h3`
  margin: 0;
  text-align: center;
  font-family: "Montserrat", sans-serif;
  font-size: 20px;
  line-height: 1.06;
  font-weight: 600;
  color: #437672;
  text-transform: uppercase;

  @media (min-width: 1024px) {
    padding-left: 0;
  }

  @media (min-width: 1181px) and (max-width: 1366px) {
    transform: translateX(-1.5rem);
  }

  @media (max-width: 767px) {
    position: relative;
    z-index: 2;
    width: min(92vw, 620px);
    margin-top: -7.1rem;
    padding: 0 2rem 2.6rem;
    font-size: 20px;
    line-height: 1.17;
  }
`;

export {
  SectionThree,
  SectionThreeInner,
  SectionThreeGrid,
  SectionThreeText,
  WarningRow,
  WarningMark,
  WarningTitle,
  BulletList,
  BulletItem,
  SectionThreeCta,
  SectionThreeVisualColumn,
  SectionThreeVisual,
  RectangleBack,
  ForegroundImage,
  PalmTitle,
};
